import { LEAGUE } from "../models/NBA"
import {
    getNbaPlayersApiV1NbaPlayersGet,
    getNbaPlayersPbpApiV1NbaPlayersPlayerIdPbpGet,
    getWnbaPlayersApiV1WnbaPlayersGet,
    getWnbaPlayersPbpApiV1WnbaPlayersPlayerIdPbpGet,
    getNbaPlayersPbpBatchApiV1NbaPlayersPbpBatchPost,
    getWnbaPlayersPbpBatchApiV1WnbaPlayersPbpBatchPost,
    Player,
    Season,
    SeasonType,
    Stat,
    GetNbaPlayersPbpApiV1NbaPlayersPlayerIdPbpGetResponse,
    GetWnbaPlayersPbpApiV1WnbaPlayersPlayerIdPbpGetResponse,
    PlayerSubQuery,
    BatchPlayerPbpRequest,
    GetNbaPlayersPbpBatchApiV1NbaPlayersPbpBatchPostResponse,
    GetWnbaPlayersPbpBatchApiV1WnbaPlayersPbpBatchPostResponse
} from "../client"
import { showErrorNotification } from "../utils/errors"

export const getPlayers = async (league: LEAGUE): Promise<Player[]> => {
    try {
        const res = league === LEAGUE.NBA ?
            await getNbaPlayersApiV1NbaPlayersGet() :
            await getWnbaPlayersApiV1WnbaPlayersGet()
        if (res.error !== undefined) throw new Error('Error getting players')
        const data = res.data as Player[]
        return data

    } catch (error) {
        showErrorNotification({
            id: 'league-data-fetch-error',
            title: 'Unable to load league data',
            message: 'Could not retrieve roster or team data. Please check your connection.',
            error
        });
        return []
    }
}

export const getPlayerPBPVideos = async (
        league: LEAGUE,
        playerId: string,
        lastNGames: string,
        categories: Stat[],
        seasons: Season[],
        seasonType: SeasonType,
        oppTeamId: string,
        cursor: string | null,
        limit: number,
        dateFrom: string,
        dateTo: string,
        periods: number[],
        order: 'asc' | 'desc',
        signal?: AbortSignal
    ): Promise<GetNbaPlayersPbpApiV1NbaPlayersPlayerIdPbpGetResponse | GetWnbaPlayersPbpApiV1WnbaPlayersPlayerIdPbpGetResponse | null> => {
    try {
        const options = {
            path: { player_id: playerId },
            query: {
                seasons,
                seasonType,
                contexts: categories,
                lastNGames: Number(lastNGames),
                opponentTeamId: oppTeamId,
                cursor,
                limit,
                dateFrom,
                dateTo,
                periods,
                order,
            },
            signal
        }

        const res = league === LEAGUE.NBA ?
            await getNbaPlayersPbpApiV1NbaPlayersPlayerIdPbpGet(options) :
            await getWnbaPlayersPbpApiV1WnbaPlayersPlayerIdPbpGet(options)

        if (res.error !== undefined) throw new Error('Error getting PBP video data for ' + playerId)
        const data = res.data
        return data

    } catch (error) {
        showErrorNotification({
            id: 'search-results-error',
            title: 'Unable to load player clips',
            message: 'Could not retrieve video highlights for this player. Please try again.',
            error
        });
        return null
    }
}

export const getBatchPlayerPBPVideos = async (
    league: LEAGUE,
    queries: PlayerSubQuery[],
    cursor: string | null,
    limit: number,
    order: 'asc' | 'desc',
    signal?: AbortSignal
): Promise<GetNbaPlayersPbpBatchApiV1NbaPlayersPbpBatchPostResponse | GetWnbaPlayersPbpBatchApiV1WnbaPlayersPbpBatchPostResponse | null> => {
    try {
        const body: BatchPlayerPbpRequest = {
            queries,
            cursor,
            limit,
            order,
        }
        const options = {
            body,
            signal
        }
        const res = league === LEAGUE.NBA
            ? await getNbaPlayersPbpBatchApiV1NbaPlayersPbpBatchPost(options)
            : await getWnbaPlayersPbpBatchApiV1WnbaPlayersPbpBatchPost(options)

        if (res.error !== undefined) throw new Error('Error getting batch player video data')
        return res.data
    } catch (error) {
        showErrorNotification({
            id: 'search-results-error',
            title: 'Unable to load player clips',
            message: 'Could not retrieve video highlights for stacked queries. Please try again.',
            error
        });
        return null
    }
}